import type { DashboardBlock } from "@zooi/contracts";
import { blockSizeLimits } from "@zooi/contracts/dashboard-layout";

/** The side or corner a resize pulls; the opposite side stays put. */
export type Edge = "n" | "e" | "s" | "w" | "nw" | "ne" | "se" | "sw";
export const EDGES: Edge[] = ["n", "e", "s", "w", "nw", "ne", "se", "sw"];

/** Snap to nearby block edges/centres before rounding to the shared 12-column grid. */
export function gestureLayout(block: DashboardBlock, others: DashboardBlock[], dx: number, dy: number, edge: Edge | null, toleranceX: number, toleranceY: number) {
  const l = block.layout;
  const magnet = (value: number, targets: number[], tolerance: number) => {
    const nearest = targets.reduce((best, target) => Math.abs(target - value) < Math.abs(best - value) ? target : best, Infinity);
    return Math.round(Math.abs(nearest - value) <= tolerance ? nearest : value);
  };
  const rest = others.filter(b => b.id !== block.id).map(b => b.layout);
  if (!edge) return {
    x: magnet(l.x + dx, rest.flatMap(r => [r.x, r.x + r.w, r.x - l.w, r.x + r.w - l.w, r.x + (r.w - l.w) / 2]), toleranceX),
    y: magnet(l.y + dy, rest.flatMap(r => [r.y, r.y + r.h, r.y - l.h, r.y + r.h - l.h, r.y + (r.h - l.h) / 2]), toleranceY),
  };
  // A pulled side snaps to other blocks' sides or to another block's size, then stays within the size limits.
  const size = blockSizeLimits(block.type), clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
  let left = l.x, right = l.x + l.w, top = l.y, bottom = l.y + l.h;
  if (edge.includes("e")) right = clamp(magnet(right + dx, rest.flatMap(r => [r.x, r.x + r.w, left + r.w]), toleranceX), left + size.minW, Math.min(12, left + size.maxW));
  if (edge.includes("w")) left = clamp(magnet(left + dx, rest.flatMap(r => [r.x, r.x + r.w, right - r.w]), toleranceX), Math.max(0, right - size.maxW), right - size.minW);
  if (edge.includes("s")) bottom = clamp(magnet(bottom + dy, rest.flatMap(r => [r.y, r.y + r.h, top + r.h]), toleranceY), top + size.minH, top + size.maxH);
  if (edge.includes("n")) top = clamp(magnet(top + dy, rest.flatMap(r => [r.y, r.y + r.h, bottom - r.h]), toleranceY), Math.max(0, bottom - size.maxH), bottom - size.minH);
  return { x: left, y: top, w: right - left, h: bottom - top };
}
